import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import type { Easing } from 'motion/react'
import Item from './Item'
import Section from './Section'

// Types
type AccordionItemId = string | number

interface TransitionConfig {
  duration?: number
  ease?: Easing
  delay?: number
}

type BodyAnimationType = 'height' | 'heightFade' | 'heightSlide' | object
type IconAnimationType = 'flip' | 'rotate'

interface AccordionContextType {
  expandedItems: Set<AccordionItemId>
  toggleItem: (id: AccordionItemId) => void
  allowMultipleExpanded: boolean
  registerItem: (
    id: AccordionItemId,
    buttonRef: React.RefObject<HTMLButtonElement | null>,
  ) => void
  unregisterItem: (id: AccordionItemId) => void
  focusItem: (id: AccordionItemId) => void
  getItemIds: () => AccordionItemId[]
  transition: TransitionConfig
  iconTransition: TransitionConfig
  sequentialAnimation: boolean
  bodyAnimation: BodyAnimationType
  iconAnimation: IconAnimationType
}

interface AccordionProps {
  children: React.ReactNode
  onChange?: (expandedIds: AccordionItemId[]) => void
  allowZeroExpanded?: boolean
  allowMultipleExpanded?: boolean
  preExpanded?: AccordionItemId[]
  className?: string
  style?: React.CSSProperties
  transition?: TransitionConfig
  iconTransition?: TransitionConfig
  sequentialAnimation?: boolean
  bodyAnimation?: BodyAnimationType
  iconAnimation?: IconAnimationType
}

// Context
const AccordionContext = createContext<AccordionContextType | null>(null)

export const useAccordionContext = () => {
  const context = useContext(AccordionContext)
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion')
  }
  return context
}

const Accordion = ({
  children,
  onChange,
  allowZeroExpanded = true,
  allowMultipleExpanded = false,
  preExpanded = [],
  className = '',
  style,
  transition = { duration: 0.2, ease: 'easeInOut' },
  iconTransition,
  sequentialAnimation = false,
  bodyAnimation = 'height',
  iconAnimation = 'flip',
  ...props
}: AccordionProps) => {
  // Default iconTransition to transition if not specified
  const effectiveIconTransition = iconTransition || transition
  const [expandedItems, setExpandedItems] = useState<Set<AccordionItemId>>(
    () => new Set(preExpanded),
  )
  const itemRefs = useRef<
    Map<AccordionItemId, React.RefObject<HTMLButtonElement | null>>
  >(new Map())

  const toggleItem = useCallback(
    (id: AccordionItemId) => {
      setExpandedItems((prev) => {
        const newSet = new Set(prev)

        if (newSet.has(id)) {
          // Collapsing an item
          newSet.delete(id)
        } else {
          // Expanding an item
          if (!allowMultipleExpanded) {
            // Clear all other expanded items if only single expansion allowed
            newSet.clear()
          }
          newSet.add(id)
        }

        // Check if we're trying to collapse all items when allowZeroExpanded is false
        if (!allowZeroExpanded && newSet.size === 0 && prev.size > 0) {
          // Don't allow collapsing if it would leave zero expanded
          return prev
        }

        return newSet
      })
    },
    [allowMultipleExpanded, allowZeroExpanded],
  )

  const registerItem = useCallback(
    (
      id: AccordionItemId,
      buttonRef: React.RefObject<HTMLButtonElement | null>,
    ) => {
      itemRefs.current.set(id, buttonRef)
    },
    [],
  )

  const unregisterItem = useCallback((id: AccordionItemId) => {
    itemRefs.current.delete(id)
  }, [])

  const getItemIds = useCallback(() => Array.from(itemRefs.current.keys()), [])

  const focusItem = useCallback((id: AccordionItemId) => {
    const buttonRef = itemRefs.current.get(id)
    if (buttonRef?.current) {
      buttonRef.current.focus()
    }
  }, [])

  // Call onChange when expandedItems changes
  useEffect(() => {
    if (onChange) {
      onChange(Array.from(expandedItems))
    }
  }, [expandedItems, onChange])

  const contextValue: AccordionContextType = {
    expandedItems,
    toggleItem,
    allowMultipleExpanded,
    registerItem,
    unregisterItem,
    focusItem,
    getItemIds,
    transition,
    iconTransition: effectiveIconTransition,
    sequentialAnimation,
    bodyAnimation,
    iconAnimation,
  }

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={className} style={style} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

Accordion.Item = Item
Accordion.Section = Section

export default Accordion
